import type { Note } from '@algo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Markdown } from '../components/Markdown';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { api, qk } from '../lib/api';

interface NoteDraft {
  id?: string;
  title: string;
  category: string;
  content: string;
  tags: string;
  topicId: string;
}

const emptyDraft = (): NoteDraft => ({
  title: '',
  category: 'Ерөнхий',
  content: '',
  tags: '',
  topicId: '',
});

function NoteEditor({
  draft,
  onClose,
  topics,
}: {
  draft: NoteDraft;
  onClose: () => void;
  topics: { id: string; name: string }[];
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(draft);
  const set = <K extends keyof NoteDraft>(key: K, value: NoteDraft[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notes'] }),
      queryClient.invalidateQueries({ queryKey: qk.tags }),
    ]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: form.title.trim(),
        category: form.category.trim() || 'Ерөнхий',
        content: form.content,
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        topicId: form.topicId || null,
      };
      return form.id ? api.updateNote(form.id, payload) : api.createNote(payload);
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteNote(form.id as string),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });

  return (
    <Modal open title={form.id ? 'Тэмдэглэл засах' : 'Шинэ тэмдэглэл'} onClose={onClose} wide>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Гарчиг" className="sm:col-span-2">
          <Input value={form.title} onChange={(event) => set('title', event.target.value)} />
        </Field>
        <Field label="Ангилал">
          <Input value={form.category} onChange={(event) => set('category', event.target.value)} />
        </Field>
        <Field label="Шошго" hint="Таслалаар тусгаарлана">
          <Input
            value={form.tags}
            onChange={(event) => set('tags', event.target.value)}
            placeholder="dp, template"
          />
        </Field>
        <Field label="Холбогдох сэдэв" className="sm:col-span-2">
          <Select value={form.topicId} onChange={(event) => set('topicId', event.target.value)}>
            <option value="">Байхгүй</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Агуулга (markdown)" className="sm:col-span-2">
          <Textarea
            rows={14}
            value={form.content}
            onChange={(event) => set('content', event.target.value)}
            placeholder={'Кодыг ``` хашилтанд бич:\n\n```cpp\nint main() {}\n```'}
          />
        </Field>
      </div>

      {save.error && <p className="mt-3 text-sm text-danger">{(save.error as Error).message}</p>}

      <div className="mt-6 flex justify-between">
        {form.id ? (
          <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
            Устгах
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Болих
          </Button>
          <Button
            variant="primary"
            disabled={!form.title.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Хадгалж байна…' : 'Тэмдэглэл хадгалах'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function KnowledgePage() {
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<NoteDraft | null>(null);

  const filters = useMemo(
    () => ({ q: search || undefined, tag: tag || undefined }),
    [search, tag],
  );

  const notesQuery = useQuery({
    queryKey: qk.notes(filters),
    queryFn: () => api.listNotes(filters),
  });
  const tagsQuery = useQuery({ queryKey: qk.tags, queryFn: () => api.listTags() });
  const topicsQuery = useQuery({ queryKey: qk.topics, queryFn: () => api.listTopics() });

  if (notesQuery.error) {
    return <ErrorState error={notesQuery.error} onRetry={() => void notesQuery.refetch()} />;
  }

  const notes = notesQuery.data ?? [];
  const selected: Note | null =
    notes.find((note) => note.id === selectedId) ?? notes[0] ?? null;

  const toDraft = (note: Note): NoteDraft => ({
    id: note.id,
    title: note.title,
    category: note.category,
    content: note.content,
    tags: note.tags.join(', '),
    topicId: note.topicId ?? '',
  });

  return (
    <>
      <PageHeader
        title="Мэдлэгийн сан"
        description="Markdown тэмдэглэл, код блок, шошго болон SQLite бүрэн текстийн хайлт"
        action={
          <Button variant="primary" onClick={() => setEditing(emptyDraft())}>
            + Шинэ тэмдэглэл
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <div className="space-y-3">
          <Input
            placeholder="Гарчиг, агуулга, шошгоор хайх…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setTag('')}
              className={clsx('chip', !tag && 'border-accent text-accent')}
            >
              Бүгд
            </button>
            {(tagsQuery.data ?? []).map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setTag(entry.name === tag ? '' : entry.name)}
                className={clsx('chip', tag === entry.name && 'border-accent text-accent')}
              >
                #{entry.name}
              </button>
            ))}
          </div>

          {notesQuery.isLoading ? (
            <Spinner label="Хайж байна…" />
          ) : notes.length === 0 ? (
            <Card>
              <EmptyState title="Тэмдэглэл олдсонгүй" description="Өөр хайлт эсвэл шошго туршиж үзнэ үү." />
            </Card>
          ) : (
            <ul className="space-y-1.5">
              {notes.map((note) => (
                <li key={note.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(note.id)}
                    className={clsx(
                      'w-full rounded-lg border p-3 text-left transition',
                      selected?.id === note.id
                        ? 'border-accent bg-accent/10'
                        : 'border-edge bg-panel hover:border-accent/50',
                    )}
                  >
                    <p className="truncate text-sm font-medium">{note.title}</p>
                    <p className="mt-0.5 text-xs text-muted">
                      {note.category} · {note.updatedAt.slice(0, 10)}
                    </p>
                    {note.tags.length > 0 && (
                      <p className="mt-1 truncate text-xs text-accent">
                        {note.tags.map((entry) => `#${entry}`).join(' ')}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {selected ? (
            <Card
              title={selected.title}
              subtitle={`${selected.category} · шинэчилсэн ${selected.updatedAt.slice(0, 10)}`}
              action={<Button onClick={() => setEditing(toDraft(selected))}>Засах</Button>}
            >
              <Markdown>{selected.content}</Markdown>
            </Card>
          ) : (
            <Card>
              <EmptyState
                title="Юу ч сонгоогүй"
                description="Зүүн талаас тэмдэглэл сонгох эсвэл шинээр бич."
              />
            </Card>
          )}
        </div>
      </div>

      {editing && (
        <NoteEditor
          draft={editing}
          topics={topicsQuery.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
